/* eslint-disable no-undef */
import { useRef, useEffect, useCallback, useState } from 'react'

export interface PullToRefreshConfig {
  /** Callback when pull-to-refresh is triggered */
  onRefresh: () => Promise<void>
  /** Pull distance threshold to trigger refresh (default: 80) */
  threshold?: number
  /** Maximum pull distance (default: 150) */
  maxPull?: number
  /** Whether pull-to-refresh is enabled (default: true) */
  enabled?: boolean
  /** Controlled refreshing state (optional) */
  refreshing?: boolean
}

export interface PullToRefreshState {
  /** Whether currently pulling */
  isPulling: boolean
  /** Whether refresh is in progress */
  isRefreshing: boolean
  /** Current pull distance in pixels */
  pullDistance: number
  /** Pull progress from 0-1 (1 = threshold reached) */
  progress: number
  /** Whether threshold has been reached */
  canRelease: boolean
}

export interface PullToRefreshReturn extends PullToRefreshState {
  /** Ref to attach to the scrollable container */
  containerRef: React.RefObject<HTMLElement>
  /** Manually reset the pull state */
  reset: () => void
}

/**
 * Hook for implementing pull-to-refresh functionality.
 *
 * @example
 * ```tsx
 * const { containerRef, isPulling, progress, isRefreshing } = usePullToRefresh({
 *   onRefresh: async () => {
 *     await fetchData()
 *   },
 *   threshold: 80,
 * })
 *
 * return (
 *   <ScrollContainer ref={containerRef}>
 *     {isRefreshing && <LoadingSpinner />}
 *     <Content style={{ transform: `translateY(${pullDistance}px)` }} />
 *   </ScrollContainer>
 * )
 * ```
 */
export const usePullToRefresh = (
  config: PullToRefreshConfig
): PullToRefreshReturn => {
  const {
    onRefresh,
    threshold = 80,
    maxPull = 150,
    enabled = true,
    refreshing: controlledRefreshing,
  } = config

  const containerRef = useRef<HTMLElement>(null)
  const startY = useRef<number>(0)
  const currentY = useRef<number>(0)
  const isPullingRef = useRef<boolean>(false)

  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    isRefreshing: false,
    pullDistance: 0,
    progress: 0,
    canRelease: false,
  })

  // Use controlled refreshing state if provided
  const isRefreshing =
    controlledRefreshing !== undefined
      ? controlledRefreshing
      : state.isRefreshing

  const reset = useCallback(() => {
    isPullingRef.current = false
    setState({
      isPulling: false,
      isRefreshing: false,
      pullDistance: 0,
      progress: 0,
      canRelease: false,
    })
  }, [])

  const handleRefresh = useCallback(async () => {
    setState((prev) => ({
      ...prev,
      isPulling: false,
      isRefreshing: true,
      pullDistance: threshold * 0.75, // Keep some pull distance while refreshing
    }))

    try {
      await onRefresh()
    } finally {
      // Only reset if not controlled
      if (controlledRefreshing === undefined) {
        reset()
      }
    }
  }, [onRefresh, threshold, controlledRefreshing, reset])

  useEffect(() => {
    const container = containerRef.current
    if (!container || !enabled) return

    let rafId: number | null = null

    const handleTouchStart = (e: TouchEvent) => {
      // Only start pull if at scroll top
      if (container.scrollTop > 0) return
      if (isRefreshing) return

      const touch = e.touches[0]
      startY.current = touch.clientY
      currentY.current = touch.clientY
      isPullingRef.current = true

      setState((prev) => ({
        ...prev,
        isPulling: true,
      }))
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isPullingRef.current) return
      if (isRefreshing) return

      const touch = e.touches[0]
      currentY.current = touch.clientY
      const deltaY = currentY.current - startY.current

      // Only handle downward pull when at top
      if (deltaY < 0 || container.scrollTop > 0) {
        isPullingRef.current = false
        setState((prev) => ({
          ...prev,
          isPulling: false,
          pullDistance: 0,
          progress: 0,
          canRelease: false,
        }))
        return
      }

      // Apply resistance to pull (logarithmic feel)
      const resistance = 0.5
      const pullDistance = Math.min(deltaY * resistance, maxPull)
      const progress = Math.min(pullDistance / threshold, 1)
      const canRelease = pullDistance >= threshold

      // Prevent scroll while pulling
      if (pullDistance > 0) {
        e.preventDefault()
      }

      setState((prev) => ({
        ...prev,
        pullDistance,
        progress,
        canRelease,
      }))
    }

    const handleTouchEnd = () => {
      if (!isPullingRef.current) return
      isPullingRef.current = false

      const deltaY = currentY.current - startY.current
      const resistance = 0.5
      const pullDistance = Math.min(deltaY * resistance, maxPull)

      if (pullDistance >= threshold && !isRefreshing) {
        handleRefresh()
      } else {
        // Animate back
        rafId = requestAnimationFrame(() => {
          setState({
            isPulling: false,
            isRefreshing: false,
            pullDistance: 0,
            progress: 0,
            canRelease: false,
          })
        })
      }
    }

    const handleTouchCancel = () => {
      isPullingRef.current = false
      setState({
        isPulling: false,
        isRefreshing: false,
        pullDistance: 0,
        progress: 0,
        canRelease: false,
      })
    }

    container.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })
    container.addEventListener('touchcancel', handleTouchCancel, {
      passive: true,
    })

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('touchcancel', handleTouchCancel)
    }
  }, [enabled, threshold, maxPull, isRefreshing, handleRefresh])

  // Handle controlled refreshing state changes
  useEffect(() => {
    if (controlledRefreshing === false && state.isRefreshing) {
      reset()
    }
  }, [controlledRefreshing, state.isRefreshing, reset])

  return {
    containerRef: containerRef as React.RefObject<HTMLElement>,
    ...state,
    isRefreshing,
    reset,
  }
}

export default usePullToRefresh
